import { Expand as ExpandIcon } from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  Button,
  Container,
  createStyles,
  Flex,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useCollections } from "@reservoir0x/reservoir-kit-ui";
import dayjs from "dayjs";

import { MINT_URL } from "~config";
import { GenArtHeaderImported } from "~features/MintPage/GenArtHeaderImported";
import { GenSeriesControls } from "~features/MintPage/components/GenSeriesControls";
import useMintState from "~hooks/useMintState";

import { CollectionContractType } from "../../../apollo/graphql.generated";
import CollectionInfo from "../CollectionInfo";
import { EditionHeader } from "../EditionHeader";
import { GenArtHeader } from "../GenArtHeader";
import MintSeriesHeader from "../header/MintSeriesHeader";
import { SaleStatus } from "../utils/types";

import { InternalLink } from "./InternalLink";
import SimplePublicUserProfile from "./SimplePublicUserProfile";

const useStyles = createStyles(() => ({
  title: {
    fontWeight: WEIGHT_BOLD,
  },
}));

export const MintHero = ({
  hideMediaHeader,
}: {
  hideMediaHeader?: boolean;
}) => {
  const { classes, theme } = useStyles();
  const {
    isAnySeriesMint,
    isSeriesMint,
    tokens,
    isCodeGenMint,
    collection,
    saleStatus,
    saleDateTo,
    totalSoldCount,
    isMarketplaceNoticeShown,
    isImported,
    reservoirCollectionId,
    mintVectorsLoading,
  } = useMintState();
  const { data: collectionsData } = useCollections({
    id: reservoirCollectionId,
  });
  const onSaleCount = parseInt(collectionsData?.[0]?.onSaleCount || "0");
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);
  const isExtraSmall = useMediaQuery(
    `(max-width: ${theme.breakpoints.xs - 1}px)`
  );

  if (!collection) {
    return null;
  }

  const marketplaceLink = MINT_URL.tokens.marketplace.replace(
    ":collectionId",
    collection.id
  );

  const viewTokensText =
    isAnySeriesMint && !mintVectorsLoading && totalSoldCount
      ? `View ${totalSoldCount} minted token${totalSoldCount !== 1 ? "s" : ""}`
      : undefined;

  const browseLinkText = viewTokensText ? (
    <Text size="sm" color={TEXT_COLOR.SECONDARY}>
      {viewTokensText} <ExpandIcon width={10} style={{ top: 3 }} />
    </Text>
  ) : undefined;

  const collectionBrowseLink = isAnySeriesMint && browseLinkText && (
    <UnstyledButton component={InternalLink} to={marketplaceLink}>
      {browseLinkText}
    </UnstyledButton>
  );

  return (
    <Stack
      align="center"
      spacing={isMobile ? 40 : 64}
      style={{ overflowX: isSeriesMint ? "hidden" : "visible" }}
      pb={hideMediaHeader ? 0 : 64} // Using padding instead of margin to avoid cutting the box shadow
      px={16}
      mx={-16}
      pt={isMobile ? 0 : 36}
    >
      <Stack align="center" spacing={16} w="100%">
        <SimplePublicUserProfile
          addressOrEns={
            collection?.creatorAddresses?.[0].address || collection?.creatorEns
          }
        />
        <Stack spacing={16} align="center" w="100%" mb={0}>
          <Title
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "100%",
            }}
            py={8}
            my={-8}
            order={isMobile ? 4 : 1}
            className={classes.title}
            ta={isAnySeriesMint ? "center" : "left"}
          >
            {collection.name}
          </Title>
          <CollectionInfo
            editionSize={
              collection.type !== CollectionContractType.General
                ? tokens[0].size
                : undefined
            }
            textProps={{ color: TEXT_COLOR.SECONDARY }}
            nonTransferable={tokens[0].nonTransferable ?? null}
            copyableAddress
            series={isAnySeriesMint}
            codeGenSeries={isCodeGenMint}
            rightContent={collectionBrowseLink}
          />
          {isMarketplaceNoticeShown && (
            <Container>
              <Paper p="md" mb="lg" radius={12} shadow="sm">
                <Flex
                  justify="space-between"
                  direction={isExtraSmall ? "column" : "row"}
                  gap={isExtraSmall ? 16 : 40}
                  align={isExtraSmall ? "" : "center"}
                >
                  <Stack spacing={4}>
                    <Text size="lg" fw={WEIGHT_BOLD}>
                      Mint ended
                    </Text>
                    <Text size="sm" color={theme.colors.secondaryText[0]}>
                      This mint{" "}
                      {saleStatus === SaleStatus.SOLD_OUT
                        ? "sold out"
                        : `ended on ${dayjs(saleDateTo).format("MMMM DD")}`}
                      , but{" "}
                      {onSaleCount
                        ? `${onSaleCount} item${
                            onSaleCount !== 1 ? "s are" : " is"
                          } listed for sale on the
                      marketplace and `
                        : ""}
                      you can make an offer on any item you like.
                    </Text>
                  </Stack>
                  <Button
                    component={InternalLink}
                    to={marketplaceLink}
                    color={SECONDARY_COLOR}
                  >
                    View on marketplace
                  </Button>
                </Flex>
              </Paper>
            </Container>
          )}
        </Stack>
      </Stack>
      {!hideMediaHeader &&
        (isCodeGenMint ? (
          isImported ? (
            <GenArtHeaderImported collection={collection} />
          ) : (
            <>
              {collection.flagVariations.enableMintGenSeriesControls && (
                <GenSeriesControls />
              )}
              <GenArtHeader collection={collection} />
            </>
          )
        ) : isSeriesMint ? (
          <MintSeriesHeader collectionId={collection.id} />
        ) : (
          <EditionHeader />
        ))}
    </Stack>
  );
};
